import { useEffect, useState } from 'react';
import { TWidget, TWidgetData } from '../../../../utility/types/dataProvider';
import { toTitleCase } from 'utility/functions';
import { formatChartValue, getChartConfig } from 'components/Dashboard/shared/utils';
import i18next from 'i18next';

type TPieChartState = {
  centerLabel: any;
  data: any;
  isLoading: boolean;
};

type TPieChartResult = {
  label: string;
  [key: string]: any;
};

const SOURCE_FIELD_KEY = 'source-field';
const MAX_LABEL_LENGTH = 16;

export const getPieCenterLabel = (data: any, key: string) =>
  data.reduce((a: any, b: any) => (a[key] > b[key] ? a : b), {});

// Formats the traffic source label.
export const formatLabel = (label: string = '', truncate = false) => {
  if (typeof label !== 'string') return label;
  if (label === '(direct)') return 'Direct';
  if (label.endsWith('.com')) return label;
  if (!truncate) return toTitleCase(label);
  return label.length > MAX_LABEL_LENGTH
    ? `${label.substring(0, MAX_LABEL_LENGTH)}...`
    : label;
};

export const formatPieCenterLabelValue = (value: number, local?: string | undefined) => {
  return formatChartValue({
    value,
    format: 'percentage',
    precision: 0,
    locale: local,
  });
};

export const getSum = (data: any, key: string): number => {
  return data.reduce((acc: number, obj: any) => {
    return acc + obj[key];
  }, 0);
};

export const useParsePieChartData = (
  widget: TWidget,
  data: TWidgetData,
): TPieChartState => {
  const [parsedData, setParsedData] = useState<TPieChartState>({
    centerLabel: null,
    data: null,
    isLoading: true,
  });
  const locale = i18next.language;

  useEffect(() => {
    if (!data?.results?.length) return;

    const dataset = data?.results;

    // Parse from the module payload.
    const { config: pieConfig } = getChartConfig(widget);
    const sourceFieldKey: string = pieConfig?.[SOURCE_FIELD_KEY];

    const centerLabelResult = getPieCenterLabel(dataset, sourceFieldKey);
    const sum: number = getSum(dataset, sourceFieldKey);
    const centerLabelRaw = centerLabelResult?.label;
    const centerLabelText =
      typeof centerLabelRaw === 'string' ? centerLabelRaw : String(centerLabelRaw ?? '');

    const formattedResults = dataset.map((result: TPieChartResult) => {
      const value: number = Number(((result[sourceFieldKey] * 100) / sum).toFixed(2));

      const percentValue = Intl.NumberFormat('en-US', {
        style: 'percent',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(value / 100);

      if (value < 1 && value > 0) {
        return {
          label: `< 1% ${formatLabel(result.label)}`,
          value,
        };
      }

      return {
        label: `${percentValue} ${formatLabel(result.label)}`,
        value,
      };
    });

    setParsedData({
      isLoading: false,
      data: formattedResults,
      centerLabel: {
        label:
          centerLabelText.length > MAX_LABEL_LENGTH - 4
            ? `${centerLabelText.substring(0, MAX_LABEL_LENGTH - 4)}...`
            : centerLabelText,
        value: formatPieCenterLabelValue(
          Number(centerLabelResult?.[sourceFieldKey] / sum),
          locale,
        ),
      },
    });
  }, [widget, data, locale]);

  // Return the parsed data.
  return parsedData;
};
